import { Localized, useLocalization } from "@fluent/react"
import { Box, Button, Chip, Dialog, Divider, Link, Rating, Stack, Typography } from "@mui/material"
import { getQuickTagShortLabel, Supplier } from "../suppliersSlice"
import BusinessIcon from '@mui/icons-material/Business'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import CategoryIcon from '@mui/icons-material/Category'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import TagIcon from '@mui/icons-material/Tag'
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react"
import { If } from "../../../app/If"
import { CreateSupplierDialog } from "../dialogs/CreateSupplierDialog"
import { CloseCollaborationDialog } from "../dialogs/CloseCollaborationDialog"
import { ActivateCollaborationDialog } from "../dialogs/ActivateCollaborationDialog"
import { DeleteSupplierDialog } from "../dialogs/DeleteSupplierDialog"
import EditIcon from '@mui/icons-material/Edit'
import { Package } from "../../permissions/usePackage"

type DialogState =
    | { type: "none" }
    | { type: "editSupplier" }
    | { type: "closeCollaboration" }
    | { type: "activateCollaboration" }
    | { type: "deleteSupplier" }
    
export const SupplierHead = ({ supplier }: { supplier: Supplier }) => {
    const { l10n } = useLocalization()
    const supplierType = supplier.supplierType as unknown
    const hasSupplierType = supplierType !== 0 && supplierType !== "0" && supplierType !== null && supplierType !== undefined && supplierType !== ""
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })

    const DialogContentDispatcher = ({ state: dialogState, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        const onClose = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (dialogState.type) {
            case "none":
                return null
            case "editSupplier":
                return (
                    <CreateSupplierDialog
                        mode="edit"
                        supplierId={supplier.id}
                        onSuccess={onSuccess}
                        onCancel={onClose}
                    />
                )
            case "closeCollaboration":
                return (
                    <CloseCollaborationDialog
                        supplierId={supplier.id}
                        onSuccess={onSuccess}
                        onClose={onClose}
                    />
                )
            case "activateCollaboration":
                return (
                    <ActivateCollaborationDialog
                        supplierId={supplier.id}
                        onSuccess={onSuccess}
                        onClose={onClose}
                    />
                )
            case "deleteSupplier":
                return (
                    <DeleteSupplierDialog
                        supplierId={supplier.id}
                        onSuccess={onSuccess}
                        onClose={onClose}
                    />
                )
        }
    }

    const handleEditSupplierData = useCallback(() => {
        setDialog({ type: "editSupplier" })
    }, [])

    const averageRating = useMemo(() => {
        if (supplier.rating.length === 0) {
            return 0
        }

        const sum = supplier.rating.reduce((acc, item) => acc + item.rating, 0)
        return sum / supplier.rating.length
    }, [supplier.rating])

    return (
        <>
            <Box sx={{ px: 0.5, py: 1.5, bgcolor: "grey.100", mb: 1 }}>
                <Box sx={{ px: 1, pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                        <Chip
                            icon={<BusinessIcon fontSize="small" />}
                            color="secondary"
                            label={<Localized id="supplier"><span>_Dostawca</span></Localized>}
                            variant="outlined"
                        />
                        <Chip
                            size="small"
                            label={<Localized id={supplier.status.toLowerCase()}><span>_{supplier.status}</span></Localized>}
                            color={supplier.status === "ACTIVE" ? "success" : "error"}
                            variant="filled"
                            sx={{ fontWeight: 700, letterSpacing: 0.4 }}
                        />
                    </Stack>
                    <Box
                        sx={(t) => ({
                            bgcolor: "white",
                            px: 1,
                            py: 1,
                            borderRadius: 1,
                            border: `1px solid ${t.palette.divider}`,
                        })}
                    >
                        <Typography sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 20 }}>
                            {supplier.name || "—"}
                        </Typography>
                        {supplier.shortName && (
                            <Typography variant="body2" color="text.secondary">
                                {supplier.shortName}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        pr: 1,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 1, sm: 2 }}
                        divider={
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: { xs: "none", sm: "block" } }}
                            />
                        }
                        alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: 1 }}>
                            <CategoryIcon fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="supplier-type">_Typ</Localized>:
                            </Typography>
                            <Typography variant="body2">
                                {hasSupplierType ? l10n.getString(`supplier-type-${supplierType}`, null, `_${supplierType}`) : "-"}
                            </Typography>
                        </Stack>
                        {supplier.quickTags && supplier.quickTags.length > 0 && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TagIcon fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="supplier-tags">
                                        _Tagi
                                    </Localized>
                                    :
                                </Typography>
                                {(supplier.quickTags).map((tag) => (
                                    <Chip
                                        key={tag.quickTagId}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        label={getQuickTagShortLabel(tag)}
                                    />
                                ))}
                            </Stack>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <StarOutlineIcon fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="supplier-average-ratings">_Średnia ocen:</Localized>
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Rating readOnly value={averageRating} precision={0.1} max={5} size="small" />
                                <Typography variant="body2" color="text.secondary">
                                    {supplier.rating.length > 0 ? averageRating.toFixed(1) : "-"}
                                </Typography>
                            </Stack>
                        </Stack>
                        {supplier.city && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <LocationCityIcon fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="supplier-address-city">_Miasto</Localized>:
                                </Typography>
                                <Typography variant="body2">{supplier.city}</Typography>
                            </Stack>
                        )}
                        {supplier.email && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <AlternateEmailIcon fontSize="small" />
                                <Link
                                    href={`mailto:${supplier.email}`}
                                    underline="none"
                                >
                                    {supplier.email}
                                </Link>
                            </Stack>
                        )}
                    </Stack>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, ml: "auto" }}>
                        <Package name="CompanySupplierDictionary" access="ReadWrite">
                            <Button variant="outlined" color="secondary" startIcon={<EditIcon />} size="small" onClick={handleEditSupplierData}>
                                <Localized id="edit">_Edytuj</Localized>
                            </Button>
                            <If condition={supplier?.status === "ACTIVE"}>
                                <Button variant="outlined" color="warning" size="small" onClick={() => setDialog({ type: "closeCollaboration" })}>
                                    <Localized id="supplier-close-collaboration">_Close Collaboration</Localized>
                                </Button>
                            </If>
                            <If condition={supplier?.status === "CLOSED"}>
                                <Button variant="outlined" color="success" size="small" onClick={() => setDialog({ type: "activateCollaboration" })}>
                                    <Localized id="supplier-activate">_Activate</Localized>
                                </Button>
                            </If>
                        </Package>
                        <If condition={supplier?.status === "CLOSED"}>
                            <Package name="CompanySupplierDictionary" access="ReadWrite">
                                <Button variant="outlined" color="error" size="small" onClick={() => setDialog({ type: "deleteSupplier" })}>
                                    <Localized id="supplier-delete">_Delete</Localized>
                                </Button>
                            </Package>
                        </If>
                    </Box>
                </Box>
            </Box>

            <Dialog open={dialog.type !== "none"} fullWidth maxWidth={dialog.type === "editSupplier" ? "md" : "sm"} keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>

        </>
    );
}